import React, { useReducer, useState, useContext } from 'react';
import Link from 'next/link';
import MainLayout from 'components/layout/MainLayout';
import { ASSET_PREFIX, colors } from 'utils/constants';
import HeroSection from 'components/HeroSection';
import { NextSeo } from "next-seo";
import TreesForFutureMessageGiftCards from "components/gift-cards/TreesForFutureMessageGiftCards";
import ChooseGiftCardSection from "components/gift-cards/ChooseGiftCardSection";
import OrderPreview from "components/gift-cards/OrderPreview";
import RoundedButton from "components/RoundedButton";
import { scrollToSection, sleep } from "utils/common";
import { getCookie } from "utils/cookie";
import GlowBar from "components/GlowBar";
import AuthenticationForm from "components/AuthenticationForm";
import { OptionalRegisterFormFieldElements } from "components/authentication/RegisterForm";
import PostalEntryBanner from "components/enter/PostalEntryBanner";
import PayPalAdyenCheckout, { OrderType } from "components/payments/PayPalAdyenCheckout";
import { fetchUtmParamsJoinedWithFbDataFromLocalStorage} from "utils/analytics/analytics";
import { createGiftCardOrder } from "api/order";
import FaqSection from "components/gift-cards/FaqSection";
import Router from "next/router";
import Chevron from "components/Chevron";
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import BackButton from 'components/BackButton';

const PageState = {
  CHOOSING_GIFT_CARD: 'CHOOSING_GIFT_CARD',
  PREVIEWING_ORDER: 'PREVIEWING_ORDER',
  AUTHENTICATING: 'AUTHENTICATING',
  AUTHENTICATED: 'AUTHENTICATED',
  PAYING: 'PAYING',
};

const ActionType = {
  UpdateGiftCardProperty: 'UpdateGiftCardProperty',
};

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.UpdateGiftCardProperty:
      const newState = { ...state };
      newState.giftCardDetails[action.giftCardPropertyName] = action.value;
      return newState;

    default:
      throw new Error(`Don't know what to do this Action Type: ${action.type}`);
  }
};

const GiftCardsPage = observer(() => {
  const [state, dispatch] = useReducer(reducer, {
    giftCardDetails: {
      toName: '',
      toEmailAddress: '',
      fromName: '',
      message: '',
      cardDesign: 1,
      durationInWeeks: 2,
    },
    giftCardOrderDetails: null,
    adyenPaymentSession: null,
  });

  const { currentUser } = useUserStore();

  const [pageState, setPageState] = useState(PageState.CHOOSING_GIFT_CARD);

  return (
    <MainLayout>
      <NextSeo
        title="Gift Cards | Win Epic Prizes | DAYMADE"
        description="Give someone the chance to tick off their bucket list while planting trees in their name with a DAYMADE Gift Card"
        openGraph={{
          title: 'Gift Cards | Win Epic Prizes | DAYMADE',
          description:
            'Give someone the chance to tick off their bucket list while planting trees in their name with a DAYMADE Gift Card',
        }}
      />

      <HeroSection
        bottomBar={
          <Link href="/gift-cards/redeem">
            <a
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                width: '100%',
                zIndex: 1,
                color: colors.primaryBrandGray,
                padding: '1em',
                display: 'inline',
              }}
            >
              Already got one? <strong>Redeem it here</strong>
              <Chevron
                direction="right"
                style={{
                  color: colors.primaryBrandGray,
                  fontSize: '1.2em',
                  display: 'inline',
                  marginLeft: '0.5em',
                  verticalAlign: 'middle',
                }}
              />
            </a>
          </Link>
        }
      >
        <img
          src={`${ASSET_PREFIX}/images/gift-cards/logo.png`}
          style={{ height: '140px' }}
        />

        <h1
          style={{
            color: colors.brand.neon,
            textShadow: `0 0 8px ${colors.brand.shadow}`,
            marginTop: '0px',
            paddingLeft: '1em',
            paddingRight: '1em',
          }}
        >
          Gift <span className="blueOutlineText">Cards</span>
        </h1>
      </HeroSection>

      <div id="pageTop" />

      {pageState === PageState.CHOOSING_GIFT_CARD ? (
        <ChooseGiftCardSection
          giftCardDetails={state.giftCardDetails}
          updateGiftCardProperty={(propertyName, newValue) =>
            dispatch({
              type: ActionType.UpdateGiftCardProperty,
              giftCardPropertyName: propertyName,
              value: newValue,
            })
          }
          onContinue={() => {
            setPageState(PageState.PREVIEWING_ORDER);
            scrollToSection('#pageTop');
          }}
        />
      ) : (
        <>
          <OrderPreview
            cardDesign={state.giftCardDetails.cardDesign}
            toName={state.giftCardDetails.toName}
            toEmailAddress={state.giftCardDetails.toEmailAddress}
            fromName={state.giftCardDetails.fromName}
            message={state.giftCardDetails.message}
            durationInWeeks={state.giftCardDetails.durationInWeeks}
            withOrderCost
            headingComponent={
              <>
                <img src={`${ASSET_PREFIX}/images/gift-cards/cart.svg`} />

                <h3
                  style={{
                    margin: '0.5em 0 1.4em 0',
                    textShadow: `2px 2px ${colors.brand.pastel}`,
                  }}
                >
                  Your <strong>Order</strong>
                </h3>
              </>
            }
          />

          {pageState === PageState.PREVIEWING_ORDER && (
            <>
              <RoundedButton
                onClick={async () => {
                  if (currentUser) {
                    setPageState(PageState.PAYING);
                    await sleep(100);
                    scrollToSection('#paymentSection');
                  } else {
                    setPageState(PageState.AUTHENTICATING);
                    await sleep(100);
                    scrollToSection('#authSection');
                  }
                }}
                style={{ textTransform: 'uppercase', minWidth: '280px' }}
              >
                Checkout
              </RoundedButton>

              <PostalEntryBanner displayAsPopup />

              <BackButton
                onClick={() =>
                  setPageState(PageState.CHOOSING_GIFT_CARD) &&
                  scrollToSection('#pageTop')
                }
              />
            </>
          )}

          {[PageState.AUTHENTICATING, PageState.PAYING].includes(pageState) && (
            <div
              style={{
                background: `url("${ASSET_PREFIX}/images/landing/waves.svg")`,
                width: '100%',
              }}
            >
              <GlowBar color="rgba(220, 220, 220, 0.7)" height="20px" />

              <div
                className="paddedMaxWidthContainer"
                style={{ padding: '4em 0', margin: '0 auto' }}
                id="authSection"
              >
                <img
                  src={`${ASSET_PREFIX}/images/enter/your-details.svg`}
                  style={{ width: '35px' }}
                />

                <h3>
                  Your <strong>Details</strong>
                </h3>

                {currentUser !== null ? (
                  <p style={{ fontWeight: 'bold' }}>
                    ✔️ Logged in as {currentUser.first_name}
                  </p>
                ) : (
                  <AuthenticationForm
                    onAuthentication={async () => {
                      setPageState(PageState.PAYING);
                      await sleep(100);
                      scrollToSection('#paymentSection');
                    }}
                    excludeFormFieldsList={[
                      OptionalRegisterFormFieldElements.PHONE_NUMBER,
                      OptionalRegisterFormFieldElements.DATE_OF_BIRTH,
                    ]}
                  />
                )}
              </div>

              <GlowBar
                color="rgba(220, 220, 220, 0.7)"
                height="20px"
                direction="up"
              />
            </div>
          )}

          {pageState === PageState.PAYING && (
            <>
              <img
                src={`${ASSET_PREFIX}/images/enter/payment-details.svg`}
                style={{
                  width: '44px',
                  marginBottom: '1.5em',
                  marginTop: '4em',
                }}
                alt=""
              />

              <h3 style={{ margin: '0.2em auto 1.2em auto' }}>
                Payment <strong>Details</strong>
              </h3>

              <PayPalAdyenCheckout
                orderType={OrderType.GIFT_CARD_ORDER}
                allowPayPal
                createOrder={async () => {
                  const utmParams =
                    fetchUtmParamsJoinedWithFbDataFromLocalStorage();

                  const jwt = getCookie('jwt');
                  const result = await createGiftCardOrder(
                    jwt,
                    utmParams,
                    state.giftCardDetails
                  );

                  if (result.successful) {
                    return {
                      orderId: result.giftCardOrder.id,
                      adyenPaymentSession: result.adyenPaymentSession,
                    };
                  }
                }}
                onSuccess={async (orderId) => {
                  await Router.push(
                    `/gift-cards/confirmation?orderId=${orderId}`
                  );
                  window.scrollTo(0, 0);
                }}
              />
            </>
          )}
        </>
      )}

      {pageState === PageState.CHOOSING_GIFT_CARD && <FaqSection />}

      <TreesForFutureMessageGiftCards />

      <style jsx>{`
        h3 {
          font-size: 1.4em;
        }

        p {
          text-align: center;
          margin: 10px;
          font-size: 1.2em;
          font-weight: 600;
        }

        a {
          margin: 0;
          font-weight: bold;
        }

        :global(.footer) {
          margin-top: 0 !important;
        }
      `}</style>
    </MainLayout>
  );
});

export default GiftCardsPage;
